import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
} from 'date-fns';
import { ja } from 'date-fns/locale';

// Get the current locale from i18n if available
const getDateFnsLocale = () => {
  if (typeof window !== 'undefined' && window.i18n) {
    const currentLocale = window.i18n.locale;
    const localeMap = {
      ja: ja,
      // Add more locales here as needed
    };
    return localeMap[currentLocale];
  }
  return undefined; // Returns undefined for English (default)
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat, { locale: getDateFnsLocale() });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const locale = getDateFnsLocale();
  const messageDate = format(messageTime, dateFormat, { locale });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, h:mm a', { locale });
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  const locale = getDateFnsLocale();
  return formatDistanceToNow(unixTime, { addSuffix: true, locale });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df, { locale: getDateFnsLocale() });
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Format time to Hour, Minute and Second with i18n support
 * Localized version of @chatwoot/utils formatTime
 * @param {number} timeInSeconds - Time in seconds
 * @returns {string} Formatted time string
 */
export const formatTime = timeInSeconds => {
  // Get translation function from window.i18n if available
  const t = (key, values) => {
    if (typeof window !== 'undefined' && window.i18n) {
      return window.i18n.t(key, values);
    }
    // Fallback to English if i18n not available
    const fallbacks = {
      'TIME_UNITS.SECONDS': '{value} Sec',
      'TIME_UNITS.MINUTES': '{value} Min',
      'TIME_UNITS.MINUTES_SECONDS': '{minutes} Min {seconds} Sec',
      'TIME_UNITS.HOURS': '{value} Hr',
      'TIME_UNITS.HOURS_MINUTES': '{hours} Hr {minutes} Min',
      'TIME_UNITS.DAYS': '{value} Day',
      'TIME_UNITS.DAYS_HOURS': '{days} Day {hours} Hr',
    };
    let result = fallbacks[key] || key;
    if (values) {
      Object.keys(values).forEach(k => {
        result = result.replace(`{${k}}`, values[k]);
      });
    }
    return result;
  };

  if (timeInSeconds >= 60 && timeInSeconds < 3600) {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = minutes === 60 ? 0 : Math.floor(timeInSeconds % 60);
    if (seconds > 0) {
      return t('TIME_UNITS.MINUTES_SECONDS', { minutes, seconds });
    }
    return t('TIME_UNITS.MINUTES', { value: minutes });
  }

  if (timeInSeconds >= 3600 && timeInSeconds < 86400) {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes =
      timeInSeconds % 3600 < 60 || hours === 24
        ? 0
        : Math.floor((timeInSeconds % 3600) / 60);
    if (minutes > 0) {
      return t('TIME_UNITS.HOURS_MINUTES', { hours, minutes });
    }
    return t('TIME_UNITS.HOURS', { value: hours });
  }

  if (timeInSeconds >= 86400) {
    const days = Math.floor(timeInSeconds / 86400);
    const hours =
      timeInSeconds % 86400 < 3600 || days >= 364
        ? 0
        : Math.floor((timeInSeconds % 86400) / 3600);
    if (hours > 0) {
      return t('TIME_UNITS.DAYS_HOURS', { days, hours });
    }
    return t('TIME_UNITS.DAYS', { value: days });
  }

  return t('TIME_UNITS.SECONDS', { value: Math.floor(timeInSeconds) });
};
